'use client';

import { useState, useRef } from 'react';
import { Image, Hash, Smile, Send, Loader2 } from 'lucide-react';

interface PostComposerProps {
  onPost: (content: string) => void;
}

export default function PostComposer({ onPost }: PostComposerProps) {
  const [content, setContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const MAX_CHARS = 5000;
  const remaining = MAX_CHARS - content.length;
  const isOverLimit = remaining < 0;
  const isNearLimit = remaining < 100 && remaining >= 0;
  const canPost = content.trim().length > 0 && !isOverLimit;

  const handlePost = async () => {
    if (!canPost) return;
    setIsPosting(true);
    // Backend integration point: POST /api/posts with { content, topics }
    await new Promise((r) => setTimeout(r, 800));
    onPost(content.trim());
    setContent('');
    setIsPosting(false);
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canPost) {
      handlePost();
    }
  };

  return (
    <div className={`card-elevated p-4 transition-all duration-200 ${isFocused ? 'border-primary/40 shadow-lg shadow-primary/5' : ''}`}>
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-white text-sm font-700 flex-shrink-0">
          RK
        </div>

        {/* Composer body */}
        <div className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => !content && setIsFocused(false)}
            onKeyDown={handleKeyDown}
            placeholder="Share something useful, interesting or meaningful..."
            className="composer-textarea"
            style={{ height: isFocused ? '100px' : '48px', transition: 'height 200ms ease' }}
          />

          {/* Toolbar */}
          {isFocused && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border animate-fade-in">
              {/* Actions */}
              <div className="flex items-center gap-1">
                <button className="post-action-btn" aria-label="Add image">
                  <Image size={16} />
                </button>
                <button className="post-action-btn" aria-label="Add topic">
                  <Hash size={16} />
                </button>
                <button className="post-action-btn" aria-label="Add emoji">
                  <Smile size={16} />
                </button>
              </div>

              {/* Right: char count + post */}
              <div className="flex items-center gap-3">
                {content.length > 0 && (
                  <span className={`text-xs font-mono numeric-tabular ${
                    isOverLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-muted-foreground'
                  }`}>
                    {remaining}
                  </span>
                )}
                <button
                  onClick={handlePost}
                  disabled={!canPost || isPosting}
                  className="btn-primary py-2 px-4 text-sm"
                >
                  {isPosting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <>
                      <Send size={14} />
                      Post
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}